/* Find your size. Reads the rows of the size chart in the dialog and suggests
   one. The rules come first and touch no DOM, so scripts/size-finder.check.mjs
   can run them; the dialog wiring follows. */
(() => {
  /* Body weight a usual European men's size stands for, in kg. This table is
     ours, not the maker's: it is the one assumption behind "Usual size". */
  const USUAL_KG = { XS: 55, S: 63, M: 73, L: 83, XL: 94, XXL: 106 };

  /* Chest girth of the same sizes, in cm: the middle of each band in the European
     size standard (EN 13402-3: XS 78–86, S 86–94, M 94–102, L 102–110, XL 110–118,
     XXL 118–129). The maker's weight ranges are drawn for a slighter build, so the
     garment's own chest is checked against it. */
  const USUAL_CHEST = { XS: 82, S: 90, M: 98, L: 106, XL: 114, XXL: 123.5 };

  /* Room a garment needs over the body's chest, in cm: as cut, and the least before
     it is too tight. "As cut" picks the size; under "the least" there is no size. */
  const ROOM = { close: [4, 0], regular: [8, 4], relaxed: [12, 6], oversized: [16, 8] };

  /* Read from the chart's fit line plus the product type and title, on whole words:
     "Coated" is no coat, and a "slim bomber" is cut slim. Unsure: regular. */
  function roomFor(text) {
    const value = String(text || "").toLowerCase();
    if (/\boversized\b/.test(value)) return ROOM.oversized;
    if (/\bclose cut\b|\bslim\b/.test(value)) return ROOM.close;
    if (/\b(relaxed|boxy|dropped shoulder|reads wide|jackets?|coats?|gilets?|puffers?|parkas?|overshirts?|bombers?|hoodies?|blazers?)\b/.test(value)) return ROOM.relaxed;
    const stiff = /\bno\s+stretch|\bnon-?stretch|\bnot\s+stretch|does\s?n[o']t\s+stretch|\bstretch-free\b|\bwithout\s+stretch\b/.test(value); // "No stretch" is not stretch
    if (/\bstretch/.test(value) && !stiff) return ROOM.close;
    return ROOM.regular;
  }

  // A chest figure that is a girth: a half chest, laid flat, is under 70 cm and no use here.
  const fullChest = (band) => !!(band && band.chest && band.chest[0] >= 70);

  /* Garment waist the same sizes wear, in cm: the jeans size each buys in Europe
     (XS W28, S W30, M W32, L W34, XL W36, XXL W38) less 1 cm. Trousers are checked on it. */
  const USUAL_WAIST = { XS: 70, S: 75, M: 80, L: 85, XL: 90, XXL: 95.5 };

  // The chest (or waist) that goes with a body weight, in a straight line between the sizes above.
  function figureFor(weight, table) {
    const points = Object.keys(USUAL_KG).map((size) => [USUAL_KG[size], table[size]]);
    let at = 1;
    while (at < points.length - 1 && weight > points[at][0]) at += 1;
    const [[w1, c1], [w2, c2]] = [points[at - 1], points[at]];
    return c1 + ((weight - w1) * (c2 - c1)) / (w2 - w1);
  }

  // "66–75 kg" gives [66, 75], "up to 65 kg" [-Infinity, 65], "85 kg and up" [85, Infinity].
  function parseBand(text) {
    const value = String(text || "");
    const found = (value.match(/\d+(?:\.\d+)?/g) || []).map(Number);
    if (found.length === 2 && /\d\s*[–—-]\s*\d/.test(value) && found[0] <= found[1]) return found;
    if (found.length === 1 && /^\s*up to\b/i.test(value)) return [-Infinity, found[0]];
    if (found.length === 1 && /\band up\s*$/i.test(value)) return [found[0], Infinity];
    return null;
  }

  // A garment figure: "76 cm" gives [76, 76], an elastic waist "70–84 cm" [70, 84].
  function parseMeasure(text) {
    const value = String(text || "");
    const found = (value.match(/\d+(?:\.\d+)?/g) || []).map(Number);
    if (found.length === 2 && /\d\s*[–—-]\s*\d/.test(value) && found[0] <= found[1]) return found;
    if (found.length === 1) return [found[0], found[0]];
    return null;
  }

  // "S · 28" and "s" are the same size.
  const plain = (size) => String(size).split("·")[0].trim().toUpperCase();

  // rows: [{ size, weight, height, waist, chest }] in chart order, small to large.
  function readBands(rows) {
    const bands = rows.map((row) => ({
      size: row.size,
      weight: parseBand(row.weight),
      height: parseBand(row.height),
      waist: parseMeasure(row.waist),
      chest: parseMeasure(row.chest),
    }));
    ["weight", "height"].forEach((key) => {
      // "up to 72.5 kg" on a later row starts where the row before it ends.
      bands
        .filter((band) => band[key])
        .forEach((band, index, have) => {
          const before = index && have[index - 1][key][1];
          if (index && band[key][0] === -Infinity && before < band[key][1]) band[key] = [before, band[key][1]];
        });
    });
    return bands;
  }

  const count = (bands, key) => bands.filter((band) => band[key]).length;

  /* input: { weight, height }. Weight decides when the chart has weight bands.
     Height only settles a weight that fits two sizes. Between two sizes: the larger.
     Returns null without a usable figure, else { state, band, other }. */
  function recommend(bands, input) {
    const main = count(bands, "weight") > 1 ? "weight" : count(bands, "height") > 1 ? "height" : "";
    const value = Number(input[main]);
    if (!(value > 0)) return null;

    const have = bands.filter((band) => band[main]);
    const inside = have.filter((band) => value >= band[main][0] && value <= band[main][1]);
    const larger = have.filter((band) => band[main][0] > value);
    const smaller = have.filter((band) => band[main][1] < value);

    if (!inside.length) {
      if (!larger.length) return { state: "above", band: null, other: have[have.length - 1] };
      if (!smaller.length) return { state: "below", band: larger[0], other: null };
      // In a gap the smaller size is already too small.
      return { state: "between", band: larger[0], other: smaller[smaller.length - 1] };
    }

    const height = main === "weight" ? Number(input.height) : 0;
    const off = (band) => (band.height ? Math.max(band.height[0] - height, height - band.height[1], 0) : Infinity);

    if (inside.length > 1) {
      const least = Math.min(...inside.map(off));
      const near = height > 0 ? inside.filter((band) => off(band) === least) : inside;
      const band = near[near.length - 1];
      return {
        state: near.length < inside.length ? "by_height" : "between",
        band,
        other: inside.filter((item) => item !== band).pop(),
      };
    }

    const band = inside[0];
    if (height > 0 && band.height) {
      // Tall for the weight: the makers themselves say take the larger size (owner, 26 Oct 2026).
      const next = have[have.indexOf(band) + 1];
      if (height > band.height[1] && next && next.height) return { state: "tall", band: next, other: band };
      return { state: off(band) ? "by_weight" : "both", band, other: null };
    }
    return { state: "in_" + main, band, other: null };
  }

  /* The maker's suggestion checked against the garment: the first size whose chest
     leaves the room the cut needs, and of that and the maker's size the larger. When
     only the largest size comes close enough it "fits close"; when even that is too
     tight there is no size. A chart without a full chest figure on two rows is left
     as the maker has it. Same shape as recommend. */
  function roomCheck(bands, found, chest, room) {
    const have = bands.filter(fullChest);
    if (!found || !found.band || have.length < 2 || !(chest > 0)) return found;
    const last = have[have.length - 1];
    const cut = have.find((band) => band.chest[0] >= chest + room[0]);
    const after = (one, other) => bands.indexOf(one) > bands.indexOf(other);
    if (cut) return after(cut, found.band) ? { state: "room", band: cut, other: null } : found;
    if (after(found.band, last)) return found;
    if (last.chest[0] >= chest + room[1]) return { state: "close", band: last, other: null };
    return { state: "small", band: null, other: last };
  }

  /* Trousers: the first size whose waist reaches it (an elastic one once stretched),
     and of that and the maker's size the larger. A waistband that does not close does
     not fit, so there is no "fits close" here: no such size, no suggestion. */
  function waistCheck(bands, found, waist) {
    const have = bands.filter((band) => band.waist);
    if (!found || !found.band || have.length < 2 || !(waist > 0)) return found;
    const cut = have.find((band) => band.waist[1] >= waist);
    if (!cut) return { state: "small_waist", band: null, other: have[have.length - 1] };
    return bands.indexOf(cut) > bands.indexOf(found.band) ? { state: "room_waist", band: cut, other: null } : found;
  }

  const LETTER = /^(XS|S|M|L|XL|XXL|[2-5]XL)$/;
  const OFFER_MAX = 24; // room over the body's chest past which a size the maker does not vouch for is not offered

  // The maker's own weight band holds this body weight.
  const covers = (band, kg) => !!(band && band.weight && band.weight[0] <= kg && kg <= band.weight[1]);

  /* The EU sizes a chart can offer, XS to XXL, each with the maker size that fits
     it: the maker's weight answer (without weight bands, the smallest size with a
     full chest or a waist), then the chest and waist checks. The maker size never
     goes down from one EU size to the next. Offer rule (owner, 26 Oct 2026): a size
     is offered only when the maker's band covers the EU size's weight, or the chest
     is at most OFFER_MAX wider than that body; the offered sizes are contiguous.
     Waist-numbered charts and charts without usable figures give none. */
  function euSizes(bands, room) {
    if (bands.length < 2 || !bands.every((band) => LETTER.test(plain(band.size)))) return [];
    const weighed = count(bands, "weight") > 1;
    const usable = bands.filter((band) => fullChest(band) || band.waist);
    if (!weighed && usable.length < 2) return [];
    const first = usable[0];
    const sizes = Object.keys(USUAL_KG);
    let floor = 0;
    const list = sizes.reduce((out, size) => {
      const start = weighed ? recommend(bands, { weight: USUAL_KG[size] }) : { state: "garment", band: first, other: null };
      const found = waistCheck(bands, roomCheck(bands, start, USUAL_CHEST[size], room), USUAL_WAIST[size]);
      if (!found || !found.band) return out;
      const at = Math.max(bands.indexOf(found.band), floor);
      const band = bands[at];
      if (!covers(band, USUAL_KG[size]) && fullChest(band) && band.chest[0] - USUAL_CHEST[size] > OFFER_MAX) return out;
      floor = at;
      out.push(found.state === "close" && band === found.band ? { size, maker: band.size, close: true } : { size, maker: band.size });
      return out;
    }, []);
    // No holes: a size missing in the middle ends the list before it.
    const gap = list.findIndex((entry, i) => i > 0 && sizes.indexOf(entry.size) !== sizes.indexOf(list[i - 1].size) + 1);
    return gap === -1 ? list : list.slice(0, gap);
  }

  /* Without height or weight bands the garment itself is the measure.
     Waist (trousers): the smallest size at least WAIST_EASE cm wider than the
     customer's waist, as nothing here stretches; an elastic waist fits once its
     range reaches the waist. Chest: the customer measures a top that fits them,
     and the closest size wins, the larger on a tie. Same shape as recommend. */
  const WAIST_EASE = 2;
  const CHEST_REACH = 6; // further than this past either end of the chart: no match

  function compare(bands, key, value) {
    if (!(value > 0)) return null;
    const have = bands.filter((band) => (key === "chest" ? fullChest(band) : band[key]));
    if (have.length < 2) return null;
    const first = have[0];
    const last = have[have.length - 1];

    if (key === "waist") {
      const elastic = (band) => band.waist[0] < band.waist[1];
      const fits = (band) => (elastic(band) ? band.waist[1] >= value : band.waist[0] >= value + WAIST_EASE);
      const band = have.find(fits);
      if (!band) return { state: "above", band: null, other: last };
      const loose = band === first && band.waist[0] - value > WAIST_EASE + 4;
      return { state: loose ? "below" : elastic(band) ? "in_range" : "in_waist", band, other: null };
    }

    if (value > last.chest[0] + CHEST_REACH) return { state: "above", band: null, other: last };
    let band = first;
    have.forEach((item) => {
      if (Math.abs(item.chest[0] - value) <= Math.abs(band.chest[0] - value)) band = item;
    });
    return { state: value < first.chest[0] - CHEST_REACH ? "below" : "near_chest", band, other: null };
  }

  /* "Usual size" maps a size to a body weight, so it needs a weight band on
     every row, bands that only go up, and letter sizes. Anything less: no tab. */
  function usualSizes(bands) {
    const sound =
      bands.length > 1 &&
      bands.every((band, index) => {
        const before = index && bands[index - 1].weight;
        return (
          band.weight &&
          LETTER.test(plain(band.size)) &&
          (!index || (band.weight[0] >= before[0] && band.weight[1] >= before[1]))
        );
      });
    return sound ? Object.keys(USUAL_KG) : [];
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports = { USUAL_KG, USUAL_CHEST, USUAL_WAIST, ROOM, OFFER_MAX, roomFor, fullChest, covers, figureFor, roomCheck, waistCheck, euSizes, parseBand, parseMeasure, plain, readBands, recommend, compare, usualSizes };
    return;
  }

  window.Belnay = window.Belnay || {};
  // A re-rendered section brings its script tag again: listen once.
  if (window.Belnay.sizeFinder) return;
  window.Belnay.sizeFinder = true;

  const SIZES = Object.keys(USUAL_KG);
  // The EU body each size fits: chest (EN 13402-3) for tops, the jeans size for trousers.
  const FITS_CHEST = { XS: "78–86", S: "86–94", M: "94–102", L: "102–110", XL: "110–118", XXL: "118–129" };
  const FITS_WAIST = { XS: "W28", S: "W30", M: "W32", L: "W34", XL: "W36", XXL: "W38" };
  const euName = (size) => plain(size).replace(/^2XL$/, "XXL");
  const fill = (line, values) =>
    Object.keys(values || {}).reduce((out, key) => out.split("{" + key + "}").join(values[key] == null ? "" : String(values[key])), String(line || ""));
  // The figure a sentence quotes: "76" or, for an elastic waist, "70–84".
  const figure = (band, key) => (band && band[key] ? band[key].filter((n, i, all) => !i || n !== all[0]).join("–") : "");
  const cmOf = (text) => {
    const found = parseMeasure(text);
    return found ? found[0] : "";
  };

  const tabsOf = (finder) => Array.from(finder.querySelectorAll("[data-size-finder-tab]"));
  const pickerButtons = (finder) => Array.from(finder._picker.querySelectorAll("[data-option-size], [data-quick-view-size]"));
  const selectedButton = (root) => root.querySelector("[data-option-size].is-selected, [data-quick-view-size].is-selected");

  // The picker's button for a size: by EU size where the buttons carry one, else by value.
  function sizeButton(finder, maker, eu) {
    return pickerButtons(finder).find((button) => (eu && button.dataset.eu ? button.dataset.eu === eu : plain(button.dataset.value) === plain(maker))) || null;
  }

  /* The figures typed in, each inside its input's own min and max; anything else is
     a typo, not a body. Nothing counts until every field shown is filled (owner,
     26 Oct 2026): no answer on a weight alone when height is asked for too. */
  function readOwn(finder) {
    const own = {};
    let missing = false;
    ["height", "weight", "waist", "chest"].forEach((key) => {
      const input = finder.querySelector(`[data-size-finder-input="${key}"]`);
      const shown = input && !input.closest("[hidden]");
      const value = shown ? Number(input.value) : 0;
      own[key] = value >= Number(input && input.min) && value <= Number(input && input.max) ? value : 0;
      if (shown && !own[key]) missing = true;
    });
    return missing ? { height: 0, weight: 0, waist: 0, chest: 0 } : own;
  }

  // The rules' answer to the figures typed in, or null without a usable figure.
  function answerFor(finder, own) {
    const mode = finder._mode;
    if (mode === "body") {
      if (!(own.weight > 0) && !(own.height > 0)) return null;
      let found = recommend(finder._bands, { weight: own.weight, height: own.height });
      if (own.weight) found = roomCheck(finder._bands, found, figureFor(own.weight, USUAL_CHEST), finder._room);
      if (own.weight) found = waistCheck(finder._bands, found, figureFor(own.weight, USUAL_WAIST));
      return found;
    }
    return mode ? compare(finder._bands, mode, own[mode]) : null;
  }

  /* The EU size that stands for the rules' maker size: with several on one maker
     size, the one nearest the customer's own figure (weight, waist, or the chest of
     their top less the room the cut needs). */
  function euEntryFor(finder, found, own) {
    const list = finder._eu;
    const on = list.filter((item) => item.maker === found.band.size);
    const mode = finder._mode;
    const table = mode === "waist" ? USUAL_WAIST : mode === "chest" ? USUAL_CHEST : USUAL_KG;
    const value = mode === "waist" ? own.waist : mode === "chest" ? own.chest - finder._room[0] : own.weight;
    if (on.length > 1 && value > 0) {
      return on.reduce((best, item) => (Math.abs(table[item.size] - value) < Math.abs(table[best.size] - value) ? item : best));
    }
    const sizes = finder._bands.map((band) => band.size);
    return on[0] || list.find((item) => sizes.indexOf(item.maker) > sizes.indexOf(found.band.size)) || null;
  }

  const bandOf = (finder, maker) => finder._bands.find((band) => band.size === maker) || null;
  const guideOf = (finder, maker) => {
    const row = finder._rows[maker] || {};
    return [row.weight, row.height].filter(Boolean).join(", ");
  };

  /* The card under the verdict: what the size measures, how much room that is on
     the EU body, the maker's guide, the label when it says another size, and the
     maker's own (smaller) size when our chest rule went past it. */
  function cardFor(finder, entry, nudge) {
    const text = finder._text;
    const lines = [];
    const band = bandOf(finder, entry.maker);
    const row = finder._rows[entry.maker] || {};
    const size = entry.size;
    if (band && size && fullChest(band)) {
      const chest = band.chest[0];
      const room = chest - USUAL_CHEST[size];
      const fit = room < 6 ? text.fit_close : room < 12 ? text.fit_regular : room < 20 ? text.fit_relaxed : text.fit_wide;
      const [lo, hi] = FITS_CHEST[size].split("–").map(Number);
      const span = [Math.max(0, Math.round(chest - hi)), Math.max(0, Math.round(chest - lo))];
      lines.push(fill(text.card_chest, { cm: figure(band, "chest"), body: FITS_CHEST[size], room: span[0] === span[1] ? span[0] : span.join("–"), fit }));
    } else if (band && size && band.waist) {
      lines.push(fill(text.card_waist, { cm: figure(band, "waist"), body: FITS_WAIST[size] }));
    }
    const guide = guideOf(finder, entry.maker);
    let maker = guide ? fill(text.card_maker, { guide }) : "";
    if (row.length) maker = (maker + " " + fill(text.card_length, { cm: cmOf(row.length) })).trim();
    if (maker) lines.push(maker);
    if (size && euName(entry.maker) !== size) lines.push(fill(text.card_label, { label: entry.maker, size }));
    if (size && band && count(finder._bands, "weight") > 1) {
      // Our chest rule may sit above the maker's own answer: name that size too.
      const makers = recommend(finder._bands, { weight: USUAL_KG[size] });
      const own = makers && makers.band;
      if (own && finder._bands.indexOf(own) < finder._bands.indexOf(band) && fullChest(own)) {
        lines.push(fill(text.card_closer, { label: own.size, cm: figure(own, "chest"), weight: (finder._rows[own.size] || {}).weight || "" }));
      }
    }
    if (nudge && band && band.weight && isFinite(band.weight[1])) {
      const tall = band.height && isFinite(band.height[1]);
      lines.push(tall ? fill(text.card_nudge, { h: band.height[1], kg: band.weight[1] }) : fill(text.card_nudge_weight, { kg: band.weight[1] }));
    }
    return lines;
  }

  function render(finder) {
    const text = finder._text;
    const own = readOwn(finder);
    finder._own = own;
    const verdict = finder.querySelector("[data-size-finder-verdict]");
    const reason = finder.querySelector("[data-size-finder-reason]");
    const card = finder.querySelector("[data-size-finder-card]");
    const select = finder.querySelector("[data-size-finder-select]");
    finder._pick = null;
    finder._advised = "";
    let lines = [];
    let say = "";
    let why = "";
    let name = "";
    const found = answerFor(finder, own);

    if (finder._eu) {
      const list = finder._eu;
      const first = list[0];
      const last = list[list.length - 1];
      const usual = finder._usual ? list.find((item) => item.size === finder._usual) || null : null;
      let fig = found && found.band ? euEntryFor(finder, found, own) : null;
      // Two EU sizes on one maker size are the same piece: the usual one stands.
      if (fig && usual && fig.maker === usual.maker) fig = usual;
      let answer = null;
      if (found && !found.band) {
        say = text.no_size;
        why = fill(text.eu_up_to, { size: last.size });
      } else if (fig && usual && fig.size !== usual.size) {
        answer = fig;
        const band = bandOf(finder, fig.maker);
        const mode = finder._mode;
        say = fill(text.differ, { size: fig.size, usual: usual.size });
        if (mode === "waist") {
          why = fill(text.card_differ_waist, { size: fig.size, usual: usual.size, w: own.waist, cm: figure(band, "waist") });
        } else if (mode === "chest") {
          why = fill(text.card_differ_chest, { size: fig.size, usual: usual.size, c: own.chest, cm: figure(band, "chest") });
        } else {
          const figures = [own.height ? `${own.height} cm` : "", own.weight ? `${own.weight} kg` : ""].filter(Boolean).join(" and ");
          const ub = bandOf(finder, usual.maker);
          const values = { size: fig.size, usual: usual.size, figures, h: own.height, label: fig.maker, guide: guideOf(finder, fig.maker), ulabel: usual.maker, uguide: guideOf(finder, usual.maker), cm: figure(band, "chest"), ucm: figure(ub, "chest"), height: (finder._rows[fig.maker] || {}).height, uheight: (finder._rows[usual.maker] || {}).height };
          // Which rule moved the size: the maker's band, our room for the cut, or being tall for the weight.
          const line = found.state === "tall" ? text.card_differ_tall : found.state === "room" || found.state === "close" ? text.card_differ_room : text.card_differ;
          why = fill(line, values);
        }
        const l1 = cmOf((finder._rows[fig.maker] || {}).length);
        const l2 = cmOf((finder._rows[usual.maker] || {}).length);
        if (l1 && l2 && l1 !== l2) lines.push(fill(text.card_lengths, { size: fig.size, l1, usual: usual.size, l2 }));
      } else if (fig && usual) {
        answer = usual;
        say = fill(text.eu_yours, { size: usual.size });
        const guide = guideOf(finder, usual.maker);
        if (guide) lines.push(fill(text.card_match, { size: usual.size, guide }));
      } else if (fig) {
        answer = fig;
        say = fill(text.suggest, { size: fig.size });
      } else if (finder._usual && !usual) {
        say = SIZES.indexOf(finder._usual) < SIZES.indexOf(first.size) ? fill(text.eu_from, { size: first.size }) : fill(text.eu_up_to, { size: last.size });
      } else if (usual) {
        answer = usual;
        say = fill(text.eu_yours, { size: usual.size });
      }
      if (answer) {
        lines = lines.concat(cardFor(finder, answer, answer === usual && !fig && finder._mode === "body"));
        if (answer.close) lines.push(text.eu_close);
        finder._pick = sizeButton(finder, answer.maker, answer.size);
        finder._advised = answer.size;
        name = answer.size;
      }
    } else {
      // Maker sizes on the buttons: the chip stands for a body weight, the figures for themselves.
      let got = found;
      if (!got && finder._usual) {
        got = recommend(finder._bands, { weight: USUAL_KG[finder._usual] });
        got = roomCheck(finder._bands, got, USUAL_CHEST[finder._usual], finder._room);
        got = waistCheck(finder._bands, got, USUAL_WAIST[finder._usual]);
      }
      if (got) {
        const nameOf = (band) => {
          const button = band && sizeButton(finder, band.size);
          return button ? button.dataset.value : band ? band.size : "";
        };
        const key = finder._mode === "waist" || got.state.endsWith("_waist") ? "waist" : "chest";
        const values = {
          size: nameOf(got.band || got.other),
          other: nameOf(got.other),
          usual: finder._usual || "",
          kg: USUAL_KG[finder._usual] || "",
          cm: figure(got.band || got.other, key),
        };
        const byChip = !found && finder._usual;
        const state = byChip && got.state === "in_weight" ? text.usual_in : text[got.state];
        say = fill(got.band ? text.suggest : text.no_size, values);
        why = fill((byChip ? text.usual_is + " " : "") + state, values);
        if (got.band) {
          lines = lines.concat(cardFor(finder, { size: "", maker: got.band.size }, false));
          finder._pick = sizeButton(finder, got.band.size);
          finder._advised = nameOf(got.band);
          name = finder._advised;
        }
      }
    }

    if (finder._pick && finder._pick.classList.contains("is-unavailable")) {
      lines.push(fill(text.sold_out, { size: name }));
      finder._pick = null;
    }
    verdict.textContent = say;
    reason.textContent = why;
    card.textContent = "";
    lines.filter(Boolean).forEach((line) => {
      const p = document.createElement("p");
      p.className = "size-finder__line";
      p.textContent = line;
      card.appendChild(p);
    });
    select.hidden = !finder._pick;
    if (finder._pick) select.textContent = fill(text.select, { size: name });
    if (finder._picker !== document) paintAdvice(finder._picker);
  }

  function pressChip(finder, size) {
    finder._usual = size || "";
    finder.querySelectorAll("[data-size-finder-chip]").forEach((chip) => {
      chip.setAttribute("aria-pressed", chip.dataset.sizeFinderChip === finder._usual ? "true" : "false");
    });
  }

  function selectTab(finder, name, focus) {
    tabsOf(finder).forEach((tab) => {
      const on = tab.dataset.sizeFinderTab === name;
      tab.setAttribute("aria-selected", on ? "true" : "false");
      tab.tabIndex = on ? 0 : -1;
      if (on && focus) tab.focus();
    });
    finder.querySelectorAll("[data-size-finder-panel]").forEach((panel) => {
      panel.hidden = panel.dataset.sizeFinderPanel !== name;
    });
    finder._tab = name;
    if (name === "find") render(finder);
  }

  /* Runs each time theme.js opens a size dialog, so a quick view painted later
     needs no extra call. The trigger tells which size picker the dialog serves,
     and the "Size chart" link asks for the chart tab. A size already tapped on
     the buttons is the chip pressed when the dialog opens. */
  function setup(layer, trigger) {
    const finder = layer.querySelector("[data-size-finder]");
    if (!finder) return;
    finder._picker = (trigger && trigger.closest("[data-quick-view-product], [data-main-product]")) || document;
    if (!finder._bands) build(finder);
    finder._opened = true;
    const picked = finder._picker !== document ? selectedButton(finder._picker) : null;
    if (picked) {
      const size = finder._eu ? picked.dataset.eu : plain(picked.dataset.value);
      if (finder.querySelector(`[data-size-finder-chip="${size}"]`)) pressChip(finder, size);
    }
    const wanted = trigger && trigger.dataset.sizeChartTab;
    selectTab(finder, wanted === "chart" ? "chart" : "find", false);
  }

  function build(finder) {
    finder._text = finder.querySelector("[data-size-finder-result]").dataset;
    finder._room = roomFor(finder.dataset.sizeFinderFit);
    finder._modes = String(finder.dataset.sizeFinderModes || "").split(" ").filter(Boolean);
    // EU rows can repeat a maker size: the rules read each maker size once.
    const rows = Array.from(finder.querySelectorAll("[data-size-row]"));
    const seen = new Set();
    const unique = rows.filter((row) => !seen.has(row.dataset.sizeRow) && seen.add(row.dataset.sizeRow));
    finder._bands = readBands(
      unique.map((row) => ({
        size: row.dataset.sizeRow,
        weight: row.dataset.weight,
        height: row.dataset.height,
        waist: row.dataset.waist,
        chest: row.dataset.chest,
      }))
    );
    finder._rows = {};
    unique.forEach((row) => {
      finder._rows[row.dataset.sizeRow] = { weight: row.dataset.weight || "", height: row.dataset.height || "", length: row.dataset.length || "" };
    });
    const eu = rows.filter((row) => row.dataset.eu).map((row) => ({ size: row.dataset.eu, maker: row.dataset.sizeRow, close: row.hasAttribute("data-close") }));
    finder._eu = eu.length ? eu : null;

    const reveal = (selector) => {
      const node = finder.querySelector(selector);
      if (node) node.hidden = false;
    };
    // Ask only for what the chart can use: no height field on a weight-only chart.
    finder._mode = finder._modes.includes("body") ? "body" : finder._modes.includes("waist") ? "waist" : finder._modes.includes("chest") ? "chest" : "";
    let fields = 0;
    if (finder._mode === "body") {
      ["weight", "height"].forEach((key) => {
        if (count(finder._bands, key) < 2) return;
        reveal(`[data-size-finder-field="${key}"]`);
        fields += 1;
      });
      if (fields === 2) reveal('[data-size-finder-hint="body"]');
    } else if (finder._mode) {
      reveal(`[data-size-finder-field="${finder._mode}"]`);
      reveal(`[data-size-finder-hint="${finder._mode}"]`);
      fields = 1;
    }
    if (fields) reveal("[data-size-finder-fields]");

    const chips = finder.querySelector("[data-size-finder-chips]");
    (finder._eu ? SIZES : usualSizes(finder._bands)).forEach((size) => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "size-finder__chip";
      chip.textContent = size;
      chip.dataset.sizeFinderChip = size;
      chip.setAttribute("aria-pressed", "false");
      chips.appendChild(chip);
    });
    if (chips.children.length) reveal("[data-size-finder-usual]");
  }

  // The dialog that serves a product root, built if it has not been opened yet.
  function finderOf(root) {
    const open = root.querySelector("[data-size-chart-open]");
    const layer = open && document.getElementById(open.getAttribute("aria-controls") || "");
    const finder = layer && layer.querySelector("[data-size-finder]");
    if (!finder) return null;
    if (!finder._bands) {
      finder._picker = root;
      build(finder);
    }
    return finder;
  }

  // One fact under the buttons, "M fits a 94–102 cm chest.", before the "Not sure? Find your size." link (owner, 26 Oct 2026).
  function factsFor(finder, eu, maker) {
    const text = finder._text;
    const band = bandOf(finder, maker);
    if (band && fullChest(band)) return fill(text.facts_chest, { size: eu, body: FITS_CHEST[eu] });
    if (band && band.waist) return fill(text.facts_waist, { size: eu, body: FITS_WAIST[eu] });
    return "";
  }

  /* What the order records (owner, 26 Oct 2026): the usual size and figures the
     finder was told, what it advised, and the size chosen with its label:
     "usual XS · 176 cm · 58 kg · advised S · chose XS · label XS", or
     "chose M · label XL · finder not opened". Written to the form's hidden
     properties[_size_advice] input; the facts line under the buttons follows. */
  function adviceFor(finder, button) {
    const eu = button.dataset.eu || "";
    const maker = button.dataset.value;
    const parts = [];
    if (finder && finder._opened) {
      const own = finder._own || {};
      if (finder._usual) parts.push(`usual ${finder._usual}`);
      if (own.height) parts.push(`${own.height} cm`);
      if (own.weight) parts.push(`${own.weight} kg`);
      if (own.waist) parts.push(`${own.waist} cm waist`);
      if (own.chest) parts.push(`${own.chest} cm chest`);
      if (finder._advised) parts.push(`advised ${finder._advised}`);
    }
    parts.push(`chose ${eu || maker}`, `label ${maker}`);
    if (!finder || !finder._opened) parts.push("finder not opened");
    return parts.join(" · ");
  }

  function paintAdvice(root) {
    if (!root || root === document) return;
    const input = root.querySelector("[data-size-advice]");
    const facts = root.querySelector("[data-size-facts]");
    const line = root.querySelector("[data-eu-line]");
    const button = selectedButton(root);
    const finder = finderOf(root);
    if (!button) {
      if (input) {
        input.value = "";
        input.disabled = true;
      }
      if (facts) facts.hidden = true;
      if (line) line.hidden = false;
      return "";
    }
    const advice = adviceFor(finder, button);
    if (input) {
      input.value = advice;
      input.disabled = false;
    }
    // The line shows on every EU size tapped: a fact where the chart has one, the link always.
    const shown = facts && finder && button.dataset.eu ? factsFor(finder, button.dataset.eu, button.dataset.value) : "";
    if (facts) {
      const slot = facts.querySelector("[data-size-facts-text]");
      if (slot) slot.textContent = shown;
      facts.hidden = !(finder && button.dataset.eu);
    }
    if (line) line.hidden = !facts.hidden;
    return advice;
  }

  // For the cart code: the advice a product root would send, "" without a size.
  window.Belnay.sizeAdvice = (root) => (root && root.querySelector ? paintAdvice(root) || "" : "");
  window.Belnay.sizeAdvicePlain = (eu, maker) => `chose ${eu || maker} · label ${maker} · finder not opened`;

  document.addEventListener("size-chart:open", (event) => setup(event.target, event.detail));

  document.addEventListener("click", (event) => {
    const button = event.target.closest && event.target.closest("[data-option-size], [data-quick-view-size]");
    if (button) {
      const root = button.closest("[data-main-product], [data-quick-view-product]");
      // The picker's own handler marks the button first.
      if (root) window.setTimeout(() => paintAdvice(root), 0);
    }

    const finder = event.target.closest && event.target.closest("[data-size-finder]");
    if (!finder || !finder._bands) return;

    const tab = event.target.closest("[data-size-finder-tab]");
    if (tab) selectTab(finder, tab.dataset.sizeFinderTab, false);

    const chip = event.target.closest("[data-size-finder-chip]");
    if (chip) {
      pressChip(finder, chip.dataset.sizeFinderChip);
      render(finder);
    }

    if (event.target.closest("[data-size-finder-select]") && finder._pick) {
      // The picker's own click handler chooses the variant.
      finder._pick.click();
      window.Belnay.closeSizeCharts();
    }
  });

  document.addEventListener("input", (event) => {
    const finder = event.target.closest && event.target.closest("[data-size-finder]");
    if (finder && finder._bands) render(finder);
  });

  document.addEventListener("keydown", (event) => {
    const tab = event.target.closest && event.target.closest("[data-size-finder-tab]");
    if (!tab) return;
    const tabs = tabsOf(tab.closest("[data-size-finder]"));
    const at = tabs.indexOf(tab);
    const next = {
      ArrowRight: tabs[(at + 1) % tabs.length],
      ArrowLeft: tabs[(at - 1 + tabs.length) % tabs.length],
      Home: tabs[0],
      End: tabs[tabs.length - 1],
    }[event.key];
    if (!next) return;
    event.preventDefault();
    selectTab(tab.closest("[data-size-finder]"), next.dataset.sizeFinderTab, true);
  });

  // A size named in the URL is marked before any tap: its facts and advice show at once.
  const paintAll = () => document.querySelectorAll("[data-main-product]").forEach(paintAdvice);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", paintAll);
  else paintAll();
})();
